<script setup lang="ts">
import { SplitPanel } from '@directus/vue-split-panel';
import { computed, useTemplateRef } from 'vue';
import { SIDEBAR_DEFAULT_SIZE, SIDEBAR_MAX_SIZE, SIDEBAR_MIN_SIZE, SplitPanelPane, usePaneStore } from '#layers/ui/public';

const paneStore = usePaneStore();

const splitContainer = useTemplateRef<HTMLElement>('splitContainer');
const drawerContainer = useTemplateRef<HTMLElement>('drawerContainer');

const teleportTarget = computed(() => (paneStore.sidebarUseSplitPanel ? splitContainer.value : drawerContainer.value));
</script>

<template>
	<SplitPanel
		v-model:size="paneStore.sidebarSize"
		v-model:collapsed="paneStore.sidebarSplitPanelCollapsed"
		class="h-full"
		size-unit="px"
		collapsible
		:collapse-threshold="70"
		:min-size="SIDEBAR_MIN_SIZE"
		:max-size="SIDEBAR_MAX_SIZE"
		:snap-points="[SIDEBAR_DEFAULT_SIZE]"
		:snap-threshold="6"
		:ui="{
			divider: 'focus-visible:ring-1',
			start: 'flex flex-col pbe-2',
			end: 'flex flex-col pe-2 pbe-2',
		}"
		divider-hit-area="24px"
		:transition-duration="150"
		primary="start"
		:disabled="!paneStore.sidebarUseSplitPanel"
	>
		<template #start>
			<SplitPanelPane
				v-if="paneStore.sidebarUseSplitPanel"
				:size="paneStore.sidebarSize"
				side="start"
				class="flex flex-col"
			>
				<WorkspaceChromeSidebarHeader class="shrink-0" />

				<div ref="splitContainer" class="flex flex-1 grow flex-col min-block-0" />
			</SplitPanelPane>
		</template>

		<template #divider>
			<WorkspaceChromeDivider v-show="paneStore.sidebarUseSplitPanel" :show-border="false" :collapsed="paneStore.sidebarSplitPanelCollapsed" placement="start" />
		</template>

		<template #end>
			<WorkspaceChromePage>
				<template #header-title>
					<slot name="header-title" />
				</template>

				<slot />
			</WorkspaceChromePage>
		</template>
	</SplitPanel>

	<DDrawer
		:open="paneStore.sidebarDrawerVisible"
		direction="left"
		:handle="false"
		:ui="{ content: `w-[calc(100vw-var(--spacebar-width))] sm:w-62.5` }"
		@update:open="paneStore.sidebarDrawerVisible = $event"
	>
		<template #content>
			<div class="flex size-full flex-col overflow-hidden rounded-lg bg-default ring ring-default">
				<div ref="drawerContainer" class="flex h-full flex-col" />
			</div>
		</template>
	</DDrawer>

	<div class="hidden">
		<Teleport :to="teleportTarget" :disabled="!teleportTarget" defer>
			<slot name="sidebar" />
		</Teleport>
	</div>
</template>
